import React, { useState, useEffect, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { Badge, Card, Col, Row, Table, OverlayTrigger, Tooltip } from 'react-bootstrap'
import ReactApexChart from 'react-apexcharts'
import io from 'socket.io-client'
import Header from '../layouts/Header'
import Footer from '../layouts/Footer'
import { useLoading } from '../contexts/LoadingContext'
import axiosInstance from '../services/axiosInterceptor'
import { getToken } from '../services/authorize'
import { formatDateTH } from '../utils/dateUtils'
import { useAuth } from '../services/authProvider'
import { getAcademicReportRoleLabel, getLevelSuffix } from '../utils/roleLabels'
import ThaiDateInput from '../components/ThaiDateInput'
import { toISODate } from '../utils/thaiDate'

const API = process.env.REACT_APP_API

const STATUS_COLORS = {
  present: '#198754',
  late: '#ffc107',
  leave: '#0dcaf0',
  absent: '#dc3545',
}

// Helper component for info tooltip
const InfoTooltip = ({ text }) => (
  <OverlayTrigger placement="top" overlay={<Tooltip>{text}</Tooltip>}>
    <i className="ri-information-line ms-1 text-primary" style={{ cursor: 'help' }}></i>
  </OverlayTrigger>
)

// Period preset helper functions
const getPeriodDates = (preset) => {
  const today = new Date()
  const start = new Date(today)
  const end = new Date(today)

  switch (preset) {
    case 'thisWeek':
      start.setDate(today.getDate() - today.getDay())
      end.setDate(start.getDate() + 6)
      break
    case 'lastWeek':
      start.setDate(today.getDate() - today.getDay() - 7)
      end.setDate(start.getDate() + 6)
      break
    case 'thisMonth':
      start.setDate(1)
      end.setDate(1)
      end.setMonth(end.getMonth() + 1)
      end.setDate(0)
      break
    case 'lastMonth':
      start.setMonth(start.getMonth() - 1)
      start.setDate(1)
      end.setDate(0)
      break
    case 'last3Months':
      start.setMonth(today.getMonth() - 3)
      start.setDate(1)
      end.setDate(0)
      break
    default:
      return { start: null, end: null }
  }

  return {
    start: toISODate(start),
    end: toISODate(end),
  }
}

// การ์ดสถิติครู 1 รายการ สำหรับมุมมองมือถือ
const TeacherStatCard = ({ row, idx }) => {
  const expectedAttendance = row.total_students * row.check_days
  const absenceRate = row.total_records > 0 ? ((row.absent_count / row.total_records) * 100).toFixed(1) : 0
  const lateRate = row.total_records > 0 ? ((row.late_count / row.total_records) * 100).toFixed(1) : 0
  return (
    <div className="border rounded p-3">
      <div className="fw-semibold">#{idx + 1} {row.t_name}</div>
      <div className="fs-xs text-muted">ม. {row.class_room} · {row.subject || '-'}</div>
      <div className="mt-2 pt-2 border-top fs-sm">
        <div><span className="text-muted">นร. ทั้งหมด:</span> {row.total_students} · <span className="text-muted">เช็คแล้ว:</span> {row.check_days} ครั้ง</div>
        <div className="text-success fw-semibold">มาเรียน {row.present_count} / {expectedAttendance}</div>
        <div className="d-flex gap-3 mt-1">
          <span className="text-danger fw-semibold">ขาด {row.absent_count}</span>
          <span className="text-warning fw-semibold">สาย {row.late_count}</span>
          <span className="text-info fw-semibold">ลา {row.leave_count}</span>
        </div>
      </div>
      <div className="d-flex gap-2 mt-2 pt-2 border-top">
        <Badge bg={absenceRate > 20 ? 'danger' : absenceRate > 10 ? 'warning' : absenceRate === '0.0' ? 'success' : 'secondary'} className="fs-xs">
          % ขาด {absenceRate}%
        </Badge>
        <Badge bg={lateRate > 20 ? 'danger' : lateRate > 10 ? 'warning' : lateRate === '0.0' ? 'success' : 'secondary'} className="fs-xs">
          % สาย {lateRate}%
        </Badge>
      </div>
    </div>
  )
}

// การ์ดข้อมูลตาราง (รายวัน/สัปดาห์/เดือน) 1 รายการ สำหรับมุมมองมือถือ
const HistoryDataCard = ({ row, idx, viewMode }) => (
  <div className="border rounded p-3">
    <div className="d-flex align-items-center justify-content-between gap-2">
      <div className="fw-semibold">
        {viewMode === 'daily' && `${formatDateTH(row.att_date)} · คาบ ${row.period_order}`}
        {viewMode === 'weekly' && `${formatDateTH(row.week_start)} - ${formatDateTH(row.week_end)}`}
        {viewMode === 'monthly' && `${row.month}/${row.year}`}
      </div>
      <Badge bg={row.st_gen === 'm' ? 'primary' : row.st_gen === 'f' ? 'danger' : 'secondary'} className="fs-xs">
        {row.st_gen === 'm' ? 'ชาย' : row.st_gen === 'f' ? 'หญิง' : 'ชาย/หญิง'}
      </Badge>
    </div>
    <div className="d-flex gap-3 mt-2 pt-2 border-top fs-sm">
      <span className="text-success fw-semibold">มา {row.present_count}</span>
      <span className="text-danger fw-semibold">ขาด {row.absent_count}</span>
      <span className="text-warning fw-semibold">สาย {row.late_count}</span>
      <span className="text-info fw-semibold">ลา {row.leave_count}</span>
      <span className="text-muted">รวม {row.total_count || '-'}</span>
    </div>
  </div>
)

export default function AttendanceReportHistory() {
  const currentSkin = localStorage.getItem('skin-mode') ? 'dark' : ''
  const [skin, setSkin] = useState(currentSkin)
  const { setLoading } = useLoading()
  const { posIds } = useAuth()
  const roleLabel = getAcademicReportRoleLabel(posIds)
  const levelSuffix = getLevelSuffix(posIds)

  // State for period selection
  const [periodPreset, setPeriodPreset] = useState('thisMonth')
  const [customStartDate, setCustomStartDate] = useState(null)
  const [customEndDate, setCustomEndDate] = useState(null)

  // State for data fetching
  const [lastestYear, setLastestYear] = useState(null)
  const [classList, setClassList] = useState([])
  const [roomList, setRoomList] = useState([])

  // State for filters
  const [selectedClass, setSelectedClass] = useState(null)
  const [selectedRoom, setSelectedRoom] = useState(null)
  const [selectedPeriod, setSelectedPeriod] = useState(null)
  const [selectedGender, setSelectedGender] = useState('all')
  const [selectedStatus, setSelectedStatus] = useState(null)

  // State for aggregation level
  const [viewMode, setViewMode] = useState('monthly') // daily, weekly, monthly

  // State for data
  const [dailyData, setDailyData] = useState([])
  const [weeklyData, setWeeklyData] = useState([])
  const [monthlyData, setMonthlyData] = useState([])
  const [teacherStats, setTeacherStats] = useState([])
  const [teacherView, setTeacherView] = useState(() =>
    (typeof window !== 'undefined' && window.innerWidth < 768) ? 'card' : 'table')
  const [dataView, setDataView] = useState(() =>
    (typeof window !== 'undefined' && window.innerWidth < 768) ? 'card' : 'table')

  // State for skin toggle
  const switchSkin = (s) => {
    const tables = document.getElementsByTagName('table')
    for (const t of tables) {
      const theads = t.querySelectorAll('thead')
      if (s === 'dark') {
        theads.forEach(th => th.classList.add('table-dark'))
      } else {
        theads.forEach(th => th.classList.remove('table-dark'))
      }
    }
  }

  useEffect(() => {
    switchSkin(skin)
  }, [skin])

  // Get current date range based on preset
  const dateRange = useMemo(() => {
    if (periodPreset === 'custom') {
      return {
        start: customStartDate,
        end: customEndDate,
      }
    }
    return getPeriodDates(periodPreset)
  }, [periodPreset, customStartDate, customEndDate])

  // Fetch latest study year
  useEffect(() => {
    axiosInstance
      .get(`${API}/getLastestStudyYear`, {
        headers: { authorization: `Bearer ${getToken()}` },
      })
      .then((res) => {
        if (res.data?.year_id) {
          setLastestYear(res.data.year_id)
        }
      })
      .catch(() => {})
  }, [])

  // Fetch class and room lists
  useEffect(() => {
    if (!lastestYear) return

    Promise.all([
      axiosInstance.get(`${API}/getClassListByYear/${lastestYear}`, {
        headers: { authorization: `Bearer ${getToken()}` },
      }),
      axiosInstance.get(`${API}/getRoomListByYear/${lastestYear}`, {
        headers: { authorization: `Bearer ${getToken()}` },
      }),
    ])
      .then(([classRes, roomRes]) => {
        if (classRes.data?.length) setClassList(classRes.data)
        if (roomRes.data?.length) setRoomList(roomRes.data)
      })
      .catch(() => {})
  }, [lastestYear])

  // Fetch attendance data
  const fetchAttendanceData = () => {
    if (!lastestYear || !dateRange.start || !dateRange.end) return

    setLoading(true)

    // Build query params
    const params = new URLSearchParams()
    if (selectedClass) params.append('classId', selectedClass)
    if (selectedRoom) params.append('roomId', selectedRoom)
    if (selectedPeriod) params.append('periodId', selectedPeriod)
    if (selectedGender !== 'all') params.append('gender', selectedGender)
    if (selectedStatus) params.append('attStatus', selectedStatus)

    Promise.allSettled([
      axiosInstance.get(
        `${API}/getAttendanceHistoryByRange/${dateRange.start}/${dateRange.end}/${lastestYear}?${params}`,
        { headers: { authorization: `Bearer ${getToken()}` } }
      ),
      axiosInstance.get(
        `${API}/getAttendanceWeeklyAgg/${dateRange.start}/${dateRange.end}/${lastestYear}?${params}`,
        { headers: { authorization: `Bearer ${getToken()}` } }
      ),
      axiosInstance.get(
        `${API}/getAttendanceMonthlyAgg/${dateRange.start.split('-')[0]}/${dateRange.start.split('-')[1]}/${lastestYear}?${params}`,
        { headers: { authorization: `Bearer ${getToken()}` } }
      ),
      axiosInstance.get(
        `${API}/getTeacherAttendanceStats/${dateRange.start}/${dateRange.end}/${lastestYear}?${params}`,
        { headers: { authorization: `Bearer ${getToken()}` } }
      ),
    ])
      .then(([dailyRes, weeklyRes, monthlyRes, statsRes]) => {
        if (dailyRes.status === 'fulfilled') setDailyData(dailyRes.value.data || [])
        if (weeklyRes.status === 'fulfilled') setWeeklyData(weeklyRes.value.data || [])
        if (monthlyRes.status === 'fulfilled') setMonthlyData(monthlyRes.value.data || [])
        if (statsRes.status === 'fulfilled') setTeacherStats(statsRes.value.data || [])
      })
      .finally(() => setLoading(false))
  }

  // Reset room selection when class changes
  useEffect(() => {
    setSelectedRoom(null)
  }, [selectedClass])

  // Fetch data when filters or date range change
  useEffect(() => {
    fetchAttendanceData()
  }, [
    lastestYear,
    dateRange,
    selectedClass,
    selectedRoom,
    selectedPeriod,
    selectedGender,
    selectedStatus,
  ])

  // Socket.io real-time update listener
  useEffect(() => {
    const socket = io(process.env.REACT_APP_SOCKET_URL, { auth: { token: getToken() } })
    socket.on('refreshGeneralAttendance', () => {
      fetchAttendanceData()
    })
    return () => socket.disconnect()
  }, [lastestYear, dateRange])

  // Aggregate data for trend chart
  const getTrendChartData = () => {
    const data = viewMode === 'weekly' ? weeklyData : viewMode === 'monthly' ? monthlyData : dailyData

    if (!data || data.length === 0) {
      return { categories: [], series: [] }
    }

    // Group by date/week/month and aggregate
    const grouped = {}
    data.forEach((row) => {
      const key = viewMode === 'daily' ? row.att_date : viewMode === 'weekly' ? row.week_number : `${row.month}-${row.year}`

      if (!grouped[key]) {
        grouped[key] = {
          present: 0,
          absent: 0,
          late: 0,
          leave: 0,
        }
      }
      grouped[key].present += Number(row.present_count) || 0
      grouped[key].absent += Number(row.absent_count) || 0
      grouped[key].late += Number(row.late_count) || 0
      grouped[key].leave += Number(row.leave_count) || 0
    })

    const categories = Object.keys(grouped).sort()
    const present = categories.map((k) => grouped[k].present)
    const absent = categories.map((k) => grouped[k].absent)
    const late = categories.map((k) => grouped[k].late)
    const leave = categories.map((k) => grouped[k].leave)

    return {
      categories,
      series: [
        { name: 'มาเรียน', data: present, color: STATUS_COLORS.present },
        { name: 'ขาด', data: absent, color: STATUS_COLORS.absent },
        { name: 'สาย', data: late, color: STATUS_COLORS.late },
        { name: 'ลา', data: leave, color: STATUS_COLORS.leave },
      ],
    }
  }

  const trendChartData = getTrendChartData()
  const trendChartOptions = {
    chart: { type: 'area', stacked: false, toolbar: { show: false } },
    colors: [STATUS_COLORS.present, STATUS_COLORS.absent, STATUS_COLORS.late, STATUS_COLORS.leave],
    xaxis: {
      categories: trendChartData.categories,
      labels: { style: { fontSize: '12px' } },
    },
    yaxis: { labels: { style: { fontSize: '11px' } } },
    dataLabels: { enabled: false },
    legend: { position: 'top' },
    grid: { borderColor: 'rgba(72,94,144,0.07)' },
    tooltip: { shared: true, intersect: false },
    fill: { opacity: 0.4 },
  }

  const displayData = useMemo(() => {
    if (viewMode === 'daily') return dailyData
    if (viewMode === 'weekly') return weeklyData
    return monthlyData
  }, [viewMode, dailyData, weeklyData, monthlyData])

  // Export handlers
  const handleExportExcel = () => {
    if (!dateRange.start || !dateRange.end) return;

    const params = new URLSearchParams();
    if (selectedClass) params.append('classId', selectedClass);
    if (selectedRoom) params.append('roomId', selectedRoom);
    if (selectedPeriod) params.append('periodId', selectedPeriod);
    if (selectedGender !== 'all') params.append('gender', selectedGender);
    if (selectedStatus) params.append('attStatus', selectedStatus);

    const url = `${API}/exportAttendanceHistoryExcel/${dateRange.start}/${dateRange.end}/${lastestYear}/${viewMode}?${params}`;
    window.open(url, '_blank');
  };

  const handleExportPDF = () => {
    if (!dateRange.start || !dateRange.end) return;

    const params = new URLSearchParams();
    if (selectedClass) params.append('classId', selectedClass);
    if (selectedRoom) params.append('roomId', selectedRoom);
    if (selectedPeriod) params.append('periodId', selectedPeriod);
    if (selectedGender !== 'all') params.append('gender', selectedGender);
    if (selectedStatus) params.append('attStatus', selectedStatus);

    const url = `${API}/exportAttendanceHistoryPDF/${dateRange.start}/${dateRange.end}/${lastestYear}/${viewMode}?${params}`;
    window.open(url, '_blank');
  };

  const today = new Date().toLocaleDateString('th-TH', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })

  return (
    <React.Fragment>
      <Header onSkin={setSkin} />
      <div className="main main-app p-3 p-lg-4">
        {/* Header */}
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-4">
          <div>
            <ol className="breadcrumb fs-sm mb-1">
              <li className="breadcrumb-item">
                <Link to="#">{roleLabel}</Link>
              </li>
              <li className="breadcrumb-item active">รายงานการเข้าเรียนย้อนหลัง</li>
            </ol>
            <h4 className="main-title mb-0">สถิติการเข้าเรียนย้อนหลัง{levelSuffix}</h4>
            <p className="text-muted fs-sm mb-0">{today}</p>
          </div>
          <div className="d-flex flex-wrap align-items-center gap-2">
            {/* Period selector */}
            <select
              className="form-select form-select-sm"
              style={{ minWidth: 150, borderColor: '#0d6efd', color: '#0d6efd' }}
              value={periodPreset}
              onChange={(e) => setPeriodPreset(e.target.value)}
            >
              <option value="thisWeek">สัปดาห์นี้</option>
              <option value="lastWeek">สัปดาห์ที่แล้ว</option>
              <option value="thisMonth">เดือนนี้</option>
              <option value="lastMonth">เดือนที่แล้ว</option>
              <option value="last3Months">3 เดือนที่แล้ว</option>
              <option value="custom">กำหนดเอง</option>
            </select>

            {/* Gender filter */}
            <div className="btn-group btn-group-sm">
              {[
                ['all', 'ทั้งหมด'],
                ['m', 'ชาย'],
                ['f', 'หญิง'],
              ].map(([v, label]) => (
                <button
                  key={v}
                  className={`btn btn${selectedGender === v ? '' : '-outline'}-primary`}
                  onClick={() => setSelectedGender(v)}
                >
                  {label}
                </button>
              ))}
            </div>

            {/* Refresh button */}
            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={fetchAttendanceData}
              title="รีเฟรช"
            >
              <i className="ri-refresh-line"></i>
            </button>
          </div>
        </div>

        {/* Custom date range if selected */}
        {periodPreset === 'custom' && (
          <Row className="g-2 mb-4">
            <Col xs="auto">
              <label className="form-label fs-sm mb-1">วันที่เริ่มต้น:</label>
              <ThaiDateInput
                className="form-control-sm"
                value={customStartDate || ''}
                onChange={v => setCustomStartDate(v)}
              />
            </Col>
            <Col xs="auto">
              <label className="form-label fs-sm mb-1">วันที่สิ้นสุด:</label>
              <ThaiDateInput
                className="form-control-sm"
                value={customEndDate || ''}
                onChange={v => setCustomEndDate(v)}
              />
            </Col>
          </Row>
        )}

        {/* Filters row */}
        <Row className="g-3 mb-4">
          <Col md={3}>
            <label className="form-label fs-sm mb-1">ชั้น:</label>
            <select
              className="form-select form-select-sm"
              value={selectedClass || ''}
              onChange={(e) => setSelectedClass(e.target.value ? parseInt(e.target.value, 10) : null)}
            >
              <option value="">ทั้งหมด</option>
              {classList.map((c) => (
                <option key={c.c_id} value={c.c_id}>
                  {c.c_id}
                </option>
              ))}
            </select>
          </Col>
          <Col md={3}>
            <label className="form-label fs-sm mb-1">ห้องเรียน:</label>
            <select
              className="form-select form-select-sm"
              value={selectedRoom || ''}
              onChange={(e) => setSelectedRoom(e.target.value ? parseInt(e.target.value, 10) : null)}
              disabled={!selectedClass}
            >
              <option value="">ทั้งหมด</option>
              {roomList
                .filter((r) => r.c_id === selectedClass)
                .map((r) => (
                  <option key={r.r_id} value={r.r_id}>
                    {r.c_id}/{r.r_no}
                  </option>
                ))}
            </select>
          </Col>
          <Col md={3}>
            <label className="form-label fs-sm mb-1">คาบ:</label>
            <select
              className="form-select form-select-sm"
              value={selectedPeriod || ''}
              onChange={(e) => setSelectedPeriod(e.target.value ? parseInt(e.target.value, 10) : null)}
            >
              <option value="">ทั้งหมด</option>
              {[1, 2, 3, 4, 5, 6, 7, 8].map((p) => (
                <option key={p} value={p}>
                  คาบ {p}
                </option>
              ))}
            </select>
          </Col>
          <Col md={3}>
            <label className="form-label fs-sm mb-1">สถานะ:</label>
            <select
              className="form-select form-select-sm"
              value={selectedStatus || ''}
              onChange={(e) => setSelectedStatus(e.target.value || null)}
            >
              <option value="">ทั้งหมด</option>
              <option value="present">มาเรียน</option>
              <option value="absent">ขาด</option>
              <option value="late">สาย</option>
              <option value="leave">ลา</option>
            </select>
          </Col>
        </Row>

        {/* View mode toggle */}
        <div className="d-flex gap-2 mb-4">
          <div className="btn-group btn-group-sm">
            {[
              ['daily', 'รายวัน'],
              ['weekly', 'รายสัปดาห์'],
              ['monthly', 'รายเดือน'],
            ].map(([v, label]) => (
              <button
                key={v}
                className={`btn btn${viewMode === v ? '' : '-outline'}-secondary`}
                onClick={() => setViewMode(v)}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Export buttons */}
          <div className="btn-group btn-group-sm ms-auto">
            <button
              className="btn btn-outline-success"
              onClick={handleExportExcel}
              disabled={!dateRange.start || !dateRange.end}
              title="ส่งออกเป็น Excel"
            >
              <i className="ri-file-excel-line"></i> Excel
            </button>
            <button
              className="btn btn-outline-danger"
              onClick={handleExportPDF}
              disabled={!dateRange.start || !dateRange.end}
              title="ส่งออกเป็น PDF"
            >
              <i className="ri-file-pdf-line"></i> PDF
            </button>
          </div>
        </div>

        {/* Trend chart */}
        <Card className="card-one mb-4">
          <Card.Header>
            <Card.Title as="h6">แนวโน้มการเข้าเรียน</Card.Title>
          </Card.Header>
          <Card.Body className="p-3">
            {trendChartData.categories.length > 0 ? (
              <ReactApexChart
                series={trendChartData.series}
                options={trendChartOptions}
                type="area"
                height={300}
              />
            ) : (
              <div className="text-center text-muted py-5">ยังไม่มีข้อมูล</div>
            )}
          </Card.Body>
        </Card>

        {/* Teacher Statistics */}
        <Card className="card-one mb-4">
          <Card.Header className="d-flex flex-wrap align-items-center gap-2">
            <Card.Title as="h6" className="mb-0">สถิตินักเรียนเข้าเรียนรายครู</Card.Title>
            <Badge bg="info">{teacherStats.length} ครู</Badge>
            <div className="btn-group btn-group-sm ms-auto" role="group" aria-label="สลับมุมมอง">
              <button type="button" className={`btn btn${teacherView === 'table' ? '' : '-outline'}-primary`}
                onClick={() => setTeacherView('table')} title="มุมมองตาราง">
                <i className="ri-table-line" />
              </button>
              <button type="button" className={`btn btn${teacherView === 'card' ? '' : '-outline'}-primary`}
                onClick={() => setTeacherView('card')} title="มุมมองการ์ด">
                <i className="ri-layout-grid-line" />
              </button>
            </div>
          </Card.Header>
          <Card.Body className="p-0">
            {teacherStats.length === 0 ? (
              <div className="text-center text-muted py-4">
                <i className="ri-file-list-3-line fs-3 d-block mb-1"></i>
                ยังไม่มีข้อมูลสถิติครู
              </div>
            ) : teacherView === 'card' ? (
              <div className="p-3" style={{ maxHeight: 600, overflowY: 'auto' }}>
                <Row className="g-3">
                  {teacherStats.map((row, idx) => (
                    <Col key={idx} xs={12} md={6} xl={4}>
                      <TeacherStatCard row={row} idx={idx} />
                    </Col>
                  ))}
                </Row>
              </div>
            ) : (
              <div style={{ maxHeight: 600, overflowY: 'auto' }}>
                <Table responsive hover size="sm" className="mb-0">
                  <thead className="table-secondary sticky-top">
                    <tr>
                      <th className="text-center" style={{ width: 50 }}>
                        #
                      </th>
                      <th style={{ width: 150 }}>ชื่อครู</th>
                      <th style={{ width: 100 }}>ชั้น</th>
                      <th style={{ width: 100 }}>วิชา</th>
                      <th className="text-center" style={{ width: 70 }}>
                        นร. ทั้งหมด
                        <InfoTooltip text="จำนวนนักเรียนทั้งหมดในห้องที่ครูคนนี้สอน" />
                      </th>
                      <th className="text-center" style={{ width: 80 }}>
                        เช็คแล้ว (ครั้ง)
                        <InfoTooltip text="จำนวนวันที่ครูได้เช็คชื่อในช่วงเวลาที่เลือก" />
                      </th>
                      <th className="text-center" style={{ width: 90 }}>
                        มาเรียน
                        <InfoTooltip text="จำนวนที่นักเรียนมาเรียน / จำนวนที่ควรมาทั้งหมด เช่น เด็ก 30 คน เช็ค 5 ครั้ง = ควรมา 150" />
                      </th>
                      <th className="text-center" style={{ width: 70 }}>
                        ขาด
                        <InfoTooltip text="จำนวนครั้งที่นักเรียนขาดเรียน" />
                      </th>
                      <th className="text-center" style={{ width: 70 }}>
                        สาย
                        <InfoTooltip text="จำนวนครั้งที่นักเรียนมาสาย" />
                      </th>
                      <th className="text-center" style={{ width: 70 }}>
                        ลา
                        <InfoTooltip text="จำนวนครั้งที่นักเรียนลา" />
                      </th>
                      <th className="text-center" style={{ width: 80 }}>
                        % ขาด
                        <InfoTooltip text="เปอร์เซ็นต์การขาดเรียน — สีแดง = เกิน 20% (สูง), เหลือง = 10-20% (ปานกลาง), เขียว = ไม่มีหรือต่ำ" />
                      </th>
                      <th className="text-center" style={{ width: 80 }}>
                        % สาย
                        <InfoTooltip text="เปอร์เซ็นต์การมาสาย — สีแดง = เกิน 20% (สูง), เหลือง = 10-20% (ปานกลาง), เขียว = ไม่มีหรือต่ำ" />
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {teacherStats.map((row, idx) => {
                      const expectedAttendance = row.total_students * row.check_days
                      const absenceRate = row.total_records > 0
                        ? ((row.absent_count / row.total_records) * 100).toFixed(1)
                        : 0
                      const lateRate = row.total_records > 0
                        ? ((row.late_count / row.total_records) * 100).toFixed(1)
                        : 0
                      return (
                        <tr key={idx}>
                          <td className="text-center">{idx + 1}</td>
                          <td>{row.t_name}</td>
                          <td className="fs-sm text-muted">ม. {row.class_room}</td>
                          <td className="fs-sm text-muted">{row.subject || '-'}</td>
                          <td className="text-center fw-semibold">{row.total_students}</td>
                          <td className="text-center fw-semibold">{row.check_days}</td>
                          <td className="text-center text-success fw-semibold">{row.present_count} / {expectedAttendance}</td>
                          <td className="text-center text-danger fw-semibold">{row.absent_count}</td>
                          <td className="text-center text-warning fw-semibold">{row.late_count}</td>
                          <td className="text-center text-info fw-semibold">{row.leave_count}</td>
                          <td className="text-center">
                            <Badge
                              bg={absenceRate > 20 ? 'danger' : absenceRate > 10 ? 'warning' : absenceRate === 0 ? 'success' : 'secondary'}
                              className="fs-xs"
                            >
                              {absenceRate}%
                            </Badge>
                          </td>
                            <td className="text-center">
                                <Badge
                                  bg={lateRate > 20 ? 'danger' : lateRate > 10 ? 'warning' : lateRate === 0 ? 'success' : 'secondary'}
                                  className="fs-xs"
                                >
                                  {lateRate}%
                                </Badge>
                            </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </Table>
              </div>
            )}
          </Card.Body>
        </Card>

        {/* Data table */}
        <Card className="card-one">
          <Card.Header className="d-flex flex-wrap align-items-center gap-2">
            <Card.Title as="h6" className="mb-0">
              ตารางข้อมูล ({viewMode === 'daily' ? 'รายวัน' : viewMode === 'weekly' ? 'รายสัปดาห์' : 'รายเดือน'})
            </Card.Title>
            <Badge bg="info">{displayData.length} รายการ</Badge>
            <div className="btn-group btn-group-sm ms-auto" role="group" aria-label="สลับมุมมอง">
              <button type="button" className={`btn btn${dataView === 'table' ? '' : '-outline'}-primary`}
                onClick={() => setDataView('table')} title="มุมมองตาราง">
                <i className="ri-table-line" />
              </button>
              <button type="button" className={`btn btn${dataView === 'card' ? '' : '-outline'}-primary`}
                onClick={() => setDataView('card')} title="มุมมองการ์ด">
                <i className="ri-layout-grid-line" />
              </button>
            </div>
          </Card.Header>
          <Card.Body className="p-0">
            {displayData.length === 0 ? (
              <div className="text-center text-muted py-4">
                <i className="ri-file-list-3-line fs-3 d-block mb-1"></i>
                ยังไม่มีข้อมูลการเข้าเรียน
              </div>
            ) : dataView === 'card' ? (
              <div className="p-3" style={{ maxHeight: 600, overflowY: 'auto' }}>
                <Row className="g-3">
                  {displayData.map((row, idx) => (
                    <Col key={idx} xs={12} md={6} xl={4}>
                      <HistoryDataCard row={row} idx={idx} viewMode={viewMode} />
                    </Col>
                  ))}
                </Row>
              </div>
            ) : (
              <div style={{ maxHeight: 600, overflowY: 'auto' }}>
                <Table responsive hover size="sm" className="mb-0">
                  <thead className="table-secondary sticky-top">
                    <tr>
                      <th className="text-center" style={{ width: 80 }}>
                        #
                      </th>
                      {viewMode === 'daily' && (
                        <>
                          <th className="text-center" style={{ width: 100 }}>
                            วันที่
                          </th>
                          <th className="text-center" style={{ width: 50 }}>
                            คาบ
                          </th>
                        </>
                      )}
                      {viewMode === 'weekly' && (
                        <th className="text-center" style={{ width: 100 }}>
                          สัปดาห์
                        </th>
                      )}
                      {viewMode === 'monthly' && (
                        <th className="text-center" style={{ width: 80 }}>
                          เดือน
                        </th>
                      )}
                      <th className="text-center" style={{ width: 60 }}>
                        เพศ
                        <InfoTooltip text="เพศของนักเรียน (ชาย หรือ หญิง) ถ้าเลือกดูทั้งหมดจะรวมทั้งสองเพศ" />
                      </th>
                      <th className="text-center" style={{ width: 80 }}>
                        มาเรียน
                        <InfoTooltip text="จำนวนครั้งที่นักเรียนมาเรียน" />
                      </th>
                      <th className="text-center" style={{ width: 80 }}>
                        ขาด
                        <InfoTooltip text="จำนวนครั้งที่นักเรียนขาดเรียน" />
                      </th>
                      <th className="text-center" style={{ width: 80 }}>
                        สาย
                        <InfoTooltip text="จำนวนครั้งที่นักเรียนมาสาย" />
                      </th>
                      <th className="text-center" style={{ width: 80 }}>
                        ลา
                        <InfoTooltip text="จำนวนครั้งที่นักเรียนลา" />
                      </th>
                      <th className="text-center" style={{ width: 80 }}>
                        รวม
                        <InfoTooltip text="จำนวนนักเรียนทั้งหมดที่ถูกเช็คชื่อในแถวนี้ (ไม่ใช่ผลรวมของ มา+ขาด+สาย+ลา)" />
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayData.map((row, idx) => (
                      <tr key={idx}>
                        <td className="text-center">{idx + 1}</td>
                        {viewMode === 'daily' && (
                          <>
                            <td className="text-center text-muted fs-sm">
                              {formatDateTH(row.att_date)}
                            </td>
                            <td className="text-center fs-sm">คาบ {row.period_order}</td>
                          </>
                        )}
                        {viewMode === 'weekly' && (
                          <td className="text-center text-muted fs-sm">
                            {formatDateTH(row.week_start)} - {formatDateTH(row.week_end)}
                          </td>
                        )}
                        {viewMode === 'monthly' && (
                          <td className="text-center text-muted fs-sm">
                            {row.month}/{row.year}
                          </td>
                        )}
                        <td className="text-center">
                          <Badge bg={row.st_gen === 'm' ? 'primary' : row.st_gen === 'f' ? 'danger' : 'secondary'} className="fs-xs">
                            {row.st_gen === 'm' ? 'ชาย' : row.st_gen === 'f' ? 'หญิง' : 'ชาย/หญิง'}
                          </Badge>
                        </td>
                        <td className="text-center fw-semibold text-success">{row.present_count}</td>
                        <td className="text-center fw-semibold text-danger">{row.absent_count}</td>
                        <td className="text-center fw-semibold text-warning">{row.late_count}</td>
                        <td className="text-center fw-semibold text-info">{row.leave_count}</td>
                        <td className="text-center fw-semibold">{row.total_count || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </Card.Body>
        </Card>

        <Footer />
      </div>
    </React.Fragment>
  )
}
